import {
  Avatar,
  Box,
  FormHelperText,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ChatSquareLike } from 'Images';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useDictionary, useIsDesktop } from 'Hooks';
import { illegalChars } from 'Regex';
import { NEW_PAGE_THOUGHTS, THOUGHTS } from 'Enum/Feedback';
export interface INote {
  value: string;
  onChange: (value: string) => void;
  thoughtsValue: string;
  error: boolean;
  setError: (value: boolean) => void;
}

const MAX_LENGTH = 500;

export const Note: FC<INote> = ({
  value,
  onChange,
  thoughtsValue,
  error,
  setError,
}) => {
  const [title, setTitle] = useState<string>('');
  const [placeholder, setPlaceholder] = useState<string>('');

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Feedback).note;

  useEffect(() => {
    switch (thoughtsValue) {
      case THOUGHTS.improvement:
        setTitle(dictionary.howToImprove);
        setPlaceholder(dictionary.howToImprovePlaceholder);
        break;
      case THOUGHTS.problem:
        setTitle(dictionary.whatIsTheProblem);
        setPlaceholder(dictionary.whatIsTheProblemPlaceholder);
        break;
      case THOUGHTS.like:
        setTitle(dictionary.details);
        setPlaceholder(dictionary.detailsPlaceholder);
        break;
      case NEW_PAGE_THOUGHTS.other:
        setTitle(dictionary.whatIsTheProblem);
        setPlaceholder(dictionary.whatIsTheProblemPlaceholder);
        break;
      default:
        setTitle('');
        setPlaceholder('');
        break;
    }
    setError(false);
  }, [thoughtsValue]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange(e.target.value);
    setError(e.target.value.trim().length === 0);
  };

  return (
    <Box>
      <Typography variant={'body2'} component='label' htmlFor='request-note'>
        {title}
      </Typography>

      <TextField
        id='request-note'
        aria-describedby={error ? 'request-note-error' : undefined}
        multiline
        rows={3}
        variant='outlined'
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onInput={(e) => {
          const target = e.target as HTMLInputElement | HTMLTextAreaElement;
          target.value = target.value
            .replace(illegalChars, '')
            .slice(0, MAX_LENGTH);
        }}
        onBlur={() => {
          setError(value.trim() === '');
        }}
        error={!!error}
        helperText={`${value.length}/${MAX_LENGTH}`}
        margin='normal'
        fullWidth
        required
        slotProps={{
          htmlInput: {
            maxLength: MAX_LENGTH,
          },
          input: {
            style: {
              background: '#fff',
              border: error ? '1px solid #ec0000' : '1px solid #B7CCFF',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              height: '190px',
              padding: isDesktop ? '16px 20px 18px 24px' : '12px',
              alignItems: 'flex-end',
              gap: '12px',
              alignSelf: 'stretch',
              marginBottom: '8px',
            },
            'aria-describedby': undefined,
            startAdornment: (
              <InputAdornment
                position='start'
                sx={{
                  width: '100%',
                  alignSelf: 'stretch',
                  margin: 0,
                  padding: 0,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    padding: '8px 12px 8px 0',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '6px',
                    alignSelf: 'stretch',
                    borderRadius: '4px',
                    backgroundColor: 'primary.shade_50',
                    width: '100%',
                    cursor: 'auto',
                  }}
                >
                  <Avatar
                    src={ChatSquareLike}
                    alt=''
                    sx={{ width: 20, height: 20, flexShrink: 0 }}
                  />
                  <Typography
                    variant='body8'
                    sx={{
                      color: 'secondary.main',
                      fontSize: (isDesktop ? '16px' : '14px') + ' !important',
                    }}
                  >
                    {dictionary.clarification}
                  </Typography>
                </Box>
              </InputAdornment>
            ),
          },
          formHelperText: {
            style: {
              color: error ? '#ec0000' : '#A020FF',
              fontWeight: value.length === MAX_LENGTH ? 'bold' : undefined,
              marginRight: '26px',
              marginTop: isDesktop ? '-2px' : '-4px',
            },
            'aria-hidden': true,
          },
        }}
        sx={{
          '&.MuiFormControl-root': {
            marginBottom: 0,
          },
          '& .MuiInputBase-input': {
            zIndex: 9,
            paddingLeft: '1px',
            ...(isDesktop && {
              width: '98%',
              paddingRight: '12px',
            }),
          },
          '& .MuiFormHelperText-root': {
            position: 'absolute',
            bottom: '8px',
            right: 0,
            fontSize: isDesktop ? '18px' : '14px',
            zIndex: 0,
          },
          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& ::placeholder': {
            color: '#A6ACBC !important',
          },
        }}
      />

      {!!error && (
        <FormHelperText
          role='alert'
          aria-live='assertive'
          error={true}
          id={'request-note-error'}
          sx={{
            ml: '16px',
            fontSize: '18px',
          }}
        >
          {dictionary.errorMessage}
        </FormHelperText>
      )}
    </Box>
  );
};
